import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import { Button, Card, Input, Select, Spinner } from "@nutrayan/ui";
import { formatIndianDate } from "@nutrayan/utils";
import {
  getAdmin,
  updateAdmin,
  activateAdmin,
  deactivateAdmin,
  lockAdmin,
  unlockAdmin,
  resetAdminPassword,
  deleteAdmin,
  adminLevelLabel,
  CREATABLE_ROLES,
  type AdminView,
  type AdminCapabilityRole,
} from "@/lib/staff-api";
import { roleLabel, tenantLabel } from "@/lib/permissions";
import { useClaims } from "@/lib/use-claims";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-caption text-muted">{label}</p>
      <div className="text-body-sm text-charcoal mt-0.5">{children}</div>
    </div>
  );
}

export default function StaffDetailPage() {
  const { id } = useParams();
  const adminId = Number(id);
  const qc = useQueryClient();
  const { userId: myUserId } = useClaims();

  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<null | "suspend" | "reset" | "delete">(null);
  const navigate = useNavigate();

  const admin = useQuery({
    queryKey: ["admin", "staff-detail", adminId],
    queryFn: () => getAdmin(adminId),
    enabled: Number.isFinite(adminId),
  });

  // Shared success/error callbacks for the lifecycle mutations (plain functions,
  // not hooks — so each useMutation below is a valid top-level hook call).
  const onDone = (successMsg: string) => () => {
    toast.success(successMsg);
    qc.invalidateQueries({ queryKey: ["admin", "staff-detail", adminId] });
    qc.invalidateQueries({ queryKey: ["admin", "staff"] });
    setConfirm(null);
  };
  const onFail = (e: Error) => toast.error(e.message || "Action failed");

  const activate = useMutation({
    mutationFn: () => activateAdmin(adminId),
    onSuccess: onDone("Administrator activated"),
    onError: onFail,
  });
  const deactivate = useMutation({
    mutationFn: () => deactivateAdmin(adminId),
    onSuccess: onDone("Administrator suspended"),
    onError: onFail,
  });
  const lock = useMutation({
    mutationFn: () => lockAdmin(adminId),
    onSuccess: onDone("Account locked"),
    onError: onFail,
  });
  const unlock = useMutation({
    mutationFn: () => unlockAdmin(adminId),
    onSuccess: onDone("Account unlocked"),
    onError: onFail,
  });
  const reset = useMutation({
    mutationFn: () => resetAdminPassword(adminId),
    onSuccess: onDone("Password reset — the admin must set a new one"),
    onError: onFail,
  });
  const remove = useMutation({
    mutationFn: () => deleteAdmin(adminId),
    onSuccess: () => {
      toast.success("Administrator deleted");
      qc.invalidateQueries({ queryKey: ["admin", "staff"] });
      navigate("/staff");
    },
    onError: onFail,
  });

  const editForm = useForm<{ fullName: string; role: AdminCapabilityRole }>({
    values: {
      fullName: admin.data?.fullName ?? "",
      role: (admin.data?.role as AdminCapabilityRole) ?? "ADMIN_OPS",
    },
  });

  const save = useMutation({
    mutationFn: (v: { fullName: string; role: AdminCapabilityRole }) =>
      updateAdmin(adminId, { fullName: v.fullName.trim(), role: v.role }),
    onSuccess: () => {
      toast.success("Administrator updated");
      qc.invalidateQueries({ queryKey: ["admin", "staff-detail", adminId] });
      qc.invalidateQueries({ queryKey: ["admin", "staff"] });
      setEditing(false);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't save"),
  });

  if (admin.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  if (admin.isError || !admin.data) {
    return (
      <Card>
        <p className="text-body text-terracotta">Couldn&apos;t load this administrator.</p>
        <Link to="/staff" className="text-forest mt-3 inline-block underline">
          Back to Staff Registry
        </Link>
      </Card>
    );
  }

  const a: AdminView = admin.data;
  const isSelf = myUserId === a.id;
  const isSuspended = a.status === "SUSPENDED";
  const busy =
    activate.isPending ||
    deactivate.isPending ||
    lock.isPending ||
    unlock.isPending ||
    reset.isPending ||
    remove.isPending;

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-2">
      <Link to="/staff" className="text-caption text-forest inline-flex items-center gap-1">
        <ArrowLeft size={13} aria-hidden /> Staff Registry
      </Link>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="bg-saffron/15 text-saffron font-display flex h-12 w-12 items-center justify-center rounded-full text-lg uppercase">
              {(a.fullName ?? a.email ?? "A").slice(0, 1)}
            </span>
            <div>
              <h1 className="font-display text-display-sm text-charcoal">{a.fullName ?? "—"}</h1>
              <p className="text-body-sm text-ink">{a.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <StatusBadge status={a.status} />
            <StatusBadge status={a.passwordStatus} />
            {a.locked ? <StatusBadge status="LOCKED" /> : null}
          </div>
        </div>

        {isSelf ? (
          <p className="text-caption text-muted bg-sand mt-4 rounded-md px-3 py-2">
            This is your own account — management actions are disabled here.
          </p>
        ) : null}

        {!editing ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="Capability role">{roleLabel(a.role)}</Field>
            <Field label="Admin level">{adminLevelLabel(a.adminLevel)}</Field>
            <Field label="Tenant">{tenantLabel(a.tenantId)}</Field>
            <Field label="Created">{a.createdAt ? formatIndianDate(a.createdAt) : "—"}</Field>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={editForm.handleSubmit((v) => save.mutate(v))}>
            <div>
              <label className="text-body-sm text-ink" htmlFor="edit-name">
                Full name
              </label>
              <Input id="edit-name" {...editForm.register("fullName")} />
            </div>
            <div>
              <label className="text-body-sm text-ink" htmlFor="edit-role">
                Capability role
              </label>
              <Select id="edit-role" {...editForm.register("role")}>
                {CREATABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save changes"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Management actions */}
      {!isSelf ? (
        <Card>
          <h2 className="font-display text-heading-md text-charcoal">Manage account</h2>
          <p className="text-body-sm text-muted mt-1">
            Every action here is recorded in the security audit log.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {!editing ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit details
              </Button>
            ) : null}
            {isSuspended ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => activate.mutate()}>
                Activate
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="border-terracotta text-terracotta hover:bg-terracotta/5"
                disabled={busy}
                onClick={() => setConfirm("suspend")}
              >
                Suspend
              </Button>
            )}
            {a.locked ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => unlock.mutate()}>
                Unlock
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => lock.mutate()}>
                Lock
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirm("reset")}>
              Reset password
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-terracotta text-terracotta hover:bg-terracotta/5"
              disabled={busy}
              onClick={() => setConfirm("delete")}
            >
              Delete
            </Button>
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirm === "suspend"}
        title="Suspend administrator?"
        description="They will be signed out and unable to access the admin portal until reactivated."
        confirmLabel="Suspend"
        tone="danger"
        loading={deactivate.isPending}
        onConfirm={() => deactivate.mutate()}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "delete"}
        title="Delete this administrator?"
        description="The account is removed and all sessions revoked. The record is retained for the security audit trail; the email is freed for reuse. This cannot be undone here."
        confirmLabel="Delete"
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "reset"}
        title="Reset this admin's password?"
        description="Their current password is cleared and all sessions revoked. They must set a new password via a secure email code."
        confirmLabel="Reset password"
        tone="danger"
        loading={reset.isPending}
        onConfirm={() => reset.mutate()}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
